/**
 * Change Local Pickup → Delivery modal (customer-facing).
 *
 * Shown next to the "Delivery method" row in the order tracker when
 * the current delivery is Local Pickup and the order status is still
 * customer-eligible (`awaiting_payment`, `payment_submitted`, or
 * `payment_verified`). The parent decides eligibility — this modal
 * only owns the form, preview math, and POST call.
 *
 * Contract mirrors the backend `apply_change`:
 *   · Sticker subtotal and tax NEVER change.
 *   · USPS Stamp adds $4.00. UPS Ground adds $12.00.
 *   · USPS requires the no-tracking acknowledgment.
 *   · Full shipping address required for both methods.
 *   · Idempotent — submitting twice returns the same totals.
 */
import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Package, Loader2, X } from "lucide-react";
import { formatApiError } from "@/lib/api";
import { track, EVENT } from "@/lib/analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

export const SHIPPING_OPTIONS = [
  {
    code: "usps_stamp",
    label: "USPS Stamp",
    price: 4.00,
    desc: "Estimated delivery: 2–7 business days after shipment",
    notice: "No tracking number",
    requiresAck: true,
  },
  {
    code: "ups_ground",
    label: "UPS Ground",
    price: 12.00,
    desc: "Shipped in a box with tracking. Estimated delivery: 1–2 business days after shipment.",
    notice: null,
    requiresAck: false,
  },
];

const USPS_ACK_TEXT =
  "I understand that USPS Stamp shipping has no tracking and I accept the risk of loss or non-delivery.";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Pure preview computer — used by the modal AND by the Jest test.
 * @param {object} order  Order envelope from the tracker.
 * @param {"usps_stamp"|"ups_ground"} code
 * @returns {{ shipping: number, newTotal: number, amountPaid: number, balanceDue: number, originalTotal: number, addedShipping: number }}
 */
export function computeChangePreview(order, code) {
  const opt = SHIPPING_OPTIONS.find((o) => o.code === code);
  if (!opt) return null;
  const totals = order?.pricing_snapshot?.totals || {};
  const subtotal = Number(totals.subtotal || 0);
  const tax = Number((totals.tax || {}).amount || 0);
  const handling = Number(totals.handling_fee || 0);
  const smallOrder = Number((totals.small_order_surcharge || {}).amount || 0);
  const currentShipping = Number((totals.shipping || {}).cost || 0);
  const originalTotal = Number(totals.total || 0);
  const newTotal = round2(subtotal + smallOrder + handling + opt.price + tax);
  const addedShipping = round2(opt.price - currentShipping);
  const amountPaid = Number(order?.payment?.amount_claimed || order?.payment?.amount_received || 0);
  const balanceDue = round2(Math.max(0, newTotal - amountPaid));
  return {
    shipping: opt.price,
    newTotal,
    amountPaid,
    balanceDue,
    originalTotal,
    addedShipping,
    subtotal,
    tax,
  };
}

function round2(n) {
  return Math.round(Number(n || 0) * 100) / 100;
}

export default function ChangeToDeliveryModal({
  open, onClose, order, magicToken, publicId, onChanged,
}) {
  const [code, setCode] = useState("usps_stamp");
  const [ack, setAck] = useState(false);
  const existingAddr = (order?.customer?.shipping_address) || {};
  const [line1, setLine1] = useState(existingAddr.line1 || "");
  const [line2, setLine2] = useState(existingAddr.line2 || "");
  const [city, setCity] = useState(existingAddr.city || "");
  const [state, setState] = useState(existingAddr.state || "");
  const [postalCode, setPostalCode] = useState(existingAddr.postal_code || "");
  const [country, setCountry] = useState(existingAddr.country || "United States");
  const [busy, setBusy] = useState(false);

  const opt = useMemo(() => SHIPPING_OPTIONS.find((o) => o.code === code), [code]);
  const preview = useMemo(() => computeChangePreview(order, code), [order, code]);

  const canSubmit =
    !!line1.trim() && !!city.trim() && !!state.trim() &&
    !!postalCode.trim() && !!country.trim() &&
    (!opt.requiresAck || ack) && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/orders/${publicId}/change-to-delivery`, {
        magic_token: magicToken,
        delivery_code: code,
        shipping_line1: line1.trim(),
        shipping_line2: line2.trim() || undefined,
        shipping_city: city.trim(),
        shipping_state: state.trim(),
        shipping_postal_code: postalCode.trim(),
        shipping_country: country.trim(),
        usps_acknowledgment: ack,
      });
      const applied = r.data?.already_applied;
      // Fire ONLY on a real successful change — not on the idempotent
      // no-op path. Order id is intentionally excluded (spec §6).
      if (!applied) {
        track(EVENT.PICKUP_CHANGED_TO_DELIVERY, {
          new_delivery_method:         code,
          added_shipping:              Number(r.data?.preview?.added_shipping || 0),
          payment_state_before_change: (order?.payment_status || order?.status || "unknown"),
        }, { once: true, key: `p2d-${publicId}` });
      }
      toast.success(applied
        ? "Delivery method is already set — no charge applied."
        : "Delivery method updated.");
      onChanged?.(r.data?.order);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail)
        || "Could not update the delivery method.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog" aria-modal="true"
      data-testid="change-to-delivery-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto border-4 border-[var(--ink)]">
        <div className="flex items-start justify-between p-5 md:p-6 border-b-2 border-[var(--ink)]">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">
              Change to Delivery
            </div>
            <h2 className="font-display text-2xl md:text-3xl mt-1">Choose a shipping method</h2>
          </div>
          <button
            type="button" onClick={onClose}
            className="text-[var(--smoke)] hover:text-[var(--ink)] p-1"
            data-testid="change-delivery-close"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 md:p-6 space-y-5">
          {/* Method picker */}
          <fieldset className="space-y-3">
            <legend className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
              Shipping method
            </legend>
            {SHIPPING_OPTIONS.map((o) => (
              <label
                key={o.code}
                className={
                  "block p-4 rounded-xl border-2 cursor-pointer transition " +
                  (code === o.code
                    ? "border-[var(--panini-red)] bg-red-50"
                    : "border-[var(--stone)] hover:border-[var(--ink)]")
                }
                data-testid={`change-delivery-option-${o.code}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="radio" name="delivery_code" value={o.code}
                    checked={code === o.code}
                    onChange={() => { setCode(o.code); if (!o.requiresAck) setAck(false); }}
                    className="mt-1"
                    data-testid={`change-delivery-radio-${o.code}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <div className="font-bold flex items-center gap-2">
                        {o.code === "usps_stamp" ? <Package size={16} /> : <Truck size={16} />}
                        {o.label}
                      </div>
                      <div className="font-mono font-bold" data-testid={`change-delivery-price-${o.code}`}>
                        {fmt(o.price)}
                      </div>
                    </div>
                    <p className="text-xs text-[var(--smoke)] mt-1">{o.desc}</p>
                    {o.notice && (
                      <p className="text-xs font-bold text-amber-800 mt-1">{o.notice}</p>
                    )}
                  </div>
                </div>
              </label>
            ))}
          </fieldset>

          {/* USPS acknowledgment */}
          {opt.requiresAck && (
            <label
              className="flex items-start gap-2 p-3 border-2 border-amber-700 bg-amber-50 rounded-lg text-sm cursor-pointer"
              data-testid="change-delivery-usps-ack-label"
            >
              <input
                type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                className="mt-1"
                data-testid="change-delivery-usps-ack"
              />
              <span>{USPS_ACK_TEXT}</span>
            </label>
          )}

          {/* Shipping address */}
          <fieldset className="space-y-3">
            <legend className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">
              Shipping address
            </legend>
            <input
              type="text" placeholder="Address line 1" value={line1}
              onChange={(e) => setLine1(e.target.value)}
              className="w-full p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
              data-testid="change-delivery-line1" required
            />
            <input
              type="text" placeholder="Address line 2 (optional)" value={line2}
              onChange={(e) => setLine2(e.target.value)}
              className="w-full p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
              data-testid="change-delivery-line2"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                type="text" placeholder="City" value={city}
                onChange={(e) => setCity(e.target.value)}
                className="p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
                data-testid="change-delivery-city" required
              />
              <input
                type="text" placeholder="State" value={state}
                onChange={(e) => setState(e.target.value)}
                className="p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
                data-testid="change-delivery-state" required
              />
              <input
                type="text" placeholder="ZIP code" value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className="p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
                data-testid="change-delivery-zip" required
              />
            </div>
            <input
              type="text" placeholder="Country" value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="w-full p-2 border-2 border-[var(--stone)] rounded-lg text-sm"
              data-testid="change-delivery-country" required
            />
          </fieldset>

          {/* Preview */}
          {preview && (
            <div className="p-4 bg-slate-50 border-2 border-[var(--ink)] rounded-xl text-sm space-y-1"
                 data-testid="change-delivery-preview">
              <div className="flex justify-between">
                <span>Current delivery</span>
                <span className="font-mono">Local Pickup</span>
              </div>
              <div className="flex justify-between">
                <span>New delivery method</span>
                <span className="font-mono" data-testid="change-delivery-preview-method">{opt.label}</span>
              </div>
              <div className="flex justify-between">
                <span>Original total</span>
                <span className="font-mono" data-testid="change-delivery-preview-original">{fmt(preview.originalTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Added shipping</span>
                <span className="font-mono text-amber-800 font-bold" data-testid="change-delivery-preview-added">+{fmt(preview.addedShipping)}</span>
              </div>
              <div className="flex justify-between border-t-2 border-[var(--ink)] pt-1 mt-1">
                <span className="font-bold">New total</span>
                <span className="font-mono font-bold" data-testid="change-delivery-preview-new-total">{fmt(preview.newTotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>Amount already paid</span>
                <span className="font-mono" data-testid="change-delivery-preview-paid">{fmt(preview.amountPaid)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-bold">New balance due</span>
                <span className="font-mono font-bold text-[var(--panini-red)]" data-testid="change-delivery-preview-balance">{fmt(preview.balanceDue)}</span>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse md:flex-row gap-3 md:justify-end pt-2">
            <button
              type="button" onClick={onClose} disabled={busy}
              className="btn-ghost"
              data-testid="change-delivery-cancel"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={!canSubmit}
              className="btn bg-[var(--panini-red)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="change-delivery-confirm"
            >
              {busy ? <Loader2 className="animate-spin" size={16} /> : null}
              Confirm Delivery Change
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
